import { langMeta } from "@scoach/types";
import { Mic, Stop } from "@scoach/ui/icons";
import { useEffect, useRef, useState } from "react";

import { api } from "../lib/http.ts";
import { getStoredLang } from "../lib/i18n.ts";

interface DictateButtonProps {
  /** Called with each finalized chunk of dictated text (append to the field). */
  onText: (text: string) => void;
  /** BCP-47 language for browser speech recognition. Defaults to the user's UI language. */
  lang?: string;
  label?: string;
  className?: string;
}

/** BCP-47 default for dictation — the user's UI language, never a fixed one. */
function defaultDictationLang(): string {
  const bcp47 = langMeta(getStoredLang()).bcp47;
  // The browser Web Speech API expects `he-IL`, not the `iw-IL` alias.
  return bcp47.startsWith("iw") ? "he-IL" : bcp47;
}

// The Web Speech API isn't in the DOM lib types.
// biome-ignore lint/suspicious/noExplicitAny: browser SpeechRecognition is untyped
type AnySpeechRecognition = any;
function getSpeechRecognitionCtor(): AnySpeechRecognition | null {
  if (typeof window === "undefined") return null;
  // biome-ignore lint/suspicious/noExplicitAny: vendor-prefixed global
  const w = window as any;
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/**
 * Mic button that dictates into a summary field.
 *
 * Preferred path (Chrome/Edge): the browser's SpeechRecognition streams results
 * live — finalized phrases are appended to the field as you speak, and the
 * in-progress words show in a caption, so text appears on the summary in real
 * time. Fallback (no browser support): record a clip and transcribe once via the
 * backend Cloud STT endpoint (/stt/dictate).
 */
export function DictateButton({
  onText,
  lang = defaultDictationLang(),
  label = "Dictate",
  className,
}: DictateButtonProps) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [interim, setInterim] = useState("");

  // Browser SpeechRecognition path.
  const recognitionRef = useRef<AnySpeechRecognition | null>(null);
  const stoppedRef = useRef(false);

  // Cloud STT fallback path.
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  const supportsBrowserSR = getSpeechRecognitionCtor() !== null;

  // Stop everything on unmount.
  useEffect(() => {
    return () => {
      try {
        recognitionRef.current?.stop();
      } catch {}
      for (const t of streamRef.current?.getTracks() ?? []) t.stop();
    };
  }, []);

  // ---- Browser SpeechRecognition (real-time) ----
  function startBrowser() {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) return;
    const rec: AnySpeechRecognition = new Ctor();
    // The browser Web Speech API expects `he-IL`, not the `iw-IL` alias.
    rec.lang = lang.startsWith("iw") ? "he-IL" : lang;
    rec.continuous = true;
    rec.interimResults = true;
    stoppedRef.current = false;

    // biome-ignore lint/suspicious/noExplicitAny: SpeechRecognition event is untyped
    rec.onresult = (e: any) => {
      let live = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        const text: string = r[0]?.transcript ?? "";
        if (r.isFinal) {
          const finalText = text.trim();
          if (finalText) onText(finalText);
        } else {
          live += text;
        }
      }
      setInterim(live.trim());
    };
    rec.onerror = () => {
      /* transient (e.g. no-speech) — onend will restart or stop */
    };
    rec.onend = () => {
      // SpeechRecognition stops itself on silence; keep it going until the user
      // presses Stop.
      if (!stoppedRef.current) {
        try {
          rec.start();
        } catch {
          /* already starting */
        }
      } else {
        setInterim("");
        setRecording(false);
      }
    };

    recognitionRef.current = rec;
    try {
      rec.start();
      setRecording(true);
    } catch (err) {
      alert(`Dictation unavailable: ${(err as Error).message}`);
    }
  }

  function stopBrowser() {
    stoppedRef.current = true;
    setInterim("");
    try {
      recognitionRef.current?.stop();
    } catch {}
    setRecording(false);
  }

  // ---- Cloud STT fallback (record → transcribe on stop) ----
  async function startCloud() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const rec = new MediaRecorder(stream);
      recorderRef.current = rec;
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => void transcribeCloud();
      rec.start();
      setRecording(true);
    } catch (err) {
      alert(`Microphone unavailable: ${(err as Error).message}`);
    }
  }

  function stopCloud() {
    recorderRef.current?.stop();
    recorderRef.current = null;
    setRecording(false);
  }

  async function transcribeCloud() {
    for (const t of streamRef.current?.getTracks() ?? []) t.stop();
    streamRef.current = null;
    const blob = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || "audio/webm" });
    if (blob.size === 0) return;
    setBusy(true);
    try {
      const buf = await blob.arrayBuffer();
      let binary = "";
      const bytes = new Uint8Array(buf);
      for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
      const audioBase64 = btoa(binary);
      const { text } = await api<{ text: string }>("/stt/dictate", {
        method: "POST",
        body: { audioBase64 },
      });
      if (text) onText(text);
    } catch (err) {
      alert(`Dictation failed: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  function toggle() {
    if (recording) {
      if (supportsBrowserSR) stopBrowser();
      else stopCloud();
      return;
    }
    if (supportsBrowserSR) startBrowser();
    else void startCloud();
  }

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <button
        type="button"
        className={className ?? "pill-btn"}
        onClick={toggle}
        disabled={busy}
        title={label}
        aria-label={label}
      >
        {recording ? <Stop size={14} /> : <Mic size={14} />}{" "}
        {busy ? "Transcribing…" : recording ? "Stop" : label}
      </button>
      {recording && (
        <span style={{ fontSize: 12, color: "var(--text-3)", fontStyle: "italic" }} dir="auto">
          {interim || "Listening…"}
        </span>
      )}
    </span>
  );
}
